import { z } from 'zod';
import { AccountCode, CcyCodeSchema, MoneyStr } from './common.js';

/**
 * Поддерживаемые типы действий, которые может породить сматченное правило.
 * Расширяется добавлением нового handler-а в `apps/api/src/modules/telegram-listener/handlers/`.
 */
export const TelegramActionType = z.enum([
  'update-rate',
  'confirm-provodka',
  'create-order',
  'log-only',
  // ─── v2: новые actions ───
  'send-message',     // отправить сообщение в другой чат по шаблону
  'reject-order',     // отклонить заявку с causal-reason
  'edit-provodka',    // редактировать pending-проводку
  'custom-webhook',   // POST/PUT/PATCH на внешний URL с JSON-телом
  'update-chat-balance', // upsert баланса (chat, ccy) из распаршенного сообщения
]);
export type TelegramActionType = z.infer<typeof TelegramActionType>;

// ─── Action configs ─────────────────────────────────────────────────────────
// Каждый actionType имеет свою Zod-схему для параметров: какие named groups
// мапятся на какие поля действия. Схемы публикуются на фронт через
// /telegram/handlers, чтобы UI редактора генерировал per-type-форму.

/**
 * update-rate: из регулярки берётся валюта и курс, обновляется reference.usdt_rates.
 * Если `ccyGroup` пустой — валюта берётся из `defaultCcy`.
 */
export const UpdateRateActionConfig = z.object({
  ccyGroup: z.string().default('ccy'),
  rateGroup: z.string().default('rate'),
  defaultCcy: CcyCodeSchema.optional(),
});
export type UpdateRateActionConfig = z.infer<typeof UpdateRateActionConfig>;

/**
 * confirm-provodka: из регулярки берётся orderId (формат W-NNN) и idx (0..3).
 * Если `idxGroup` не задан или пуст — используется `defaultIdx`.
 */
export const ConfirmProvodkaActionConfig = z.object({
  orderIdGroup: z.string().default('orderId'),
  idxGroup: z.string().optional(),
  defaultIdx: z.number().int().min(0).max(3).default(0),
});
export type ConfirmProvodkaActionConfig = z.infer<typeof ConfirmProvodkaActionConfig>;

/**
 * create-order: полный маппинг сообщения → CreateOrderInput.
 *
 * Каждое поле берётся по схеме pick(captures[<x>Group], default<X>): приоритет
 * у named group из regex, затем статический default из конфига. Все новые поля
 * optional — правила со старым (skeleton) конфигом продолжают работать:
 * exGive-нога производится от clGet, оператор — фолбэк `telegram-listener:<ruleName>`.
 */
export const CreateOrderActionConfig = z.object({
  clientGroup: z.string().optional(),
  executorGroup: z.string().optional(),
  clGiveCcyGroup: z.string().optional(),
  clGiveAmtGroup: z.string().optional(),
  clGetCcyGroup: z.string().optional(),
  clGetAmtGroup: z.string().optional(),
  exGetCcyGroup: z.string().optional(),
  exGetAmtGroup: z.string().optional(),
  defaultClient: AccountCode.optional(),
  defaultExecutor: AccountCode.optional(),
  defaultClGiveCcy: CcyCodeSchema.optional(),
  defaultClGetCcy: CcyCodeSchema.optional(),
  defaultExGetCcy: CcyCodeSchema.optional(),
  // ─── v2.1: полный create-order (все поля optional — аддитивно) ───
  /** Валюта ноги exGive (idx3). Группа не задана/не захвачена → берётся clGetCcy. */
  exGiveCcyGroup: z.string().optional(),
  /** Сумма ноги exGive (idx3). Группа не задана/не захвачена → берётся clGetAmt. */
  exGiveAmtGroup: z.string().optional(),
  /** Коэффициент (наценка рефереру), проценты в диапазоне (−100; 100]. */
  coefPctGroup: z.string().optional(),
  /** Статический коэффициент, если группа не захвачена. */
  defaultCoefPct: MoneyStr.optional(),
  /** Фикс за платёжку (pass-through) в USDT, > 0. */
  fixUsdtGroup: z.string().optional(),
  /** Статический фикс в USDT, если группа не захвачена. */
  defaultFixUsdt: MoneyStr.optional(),
  /** Бонус исполнителю в USDT, > 0 (вычитается из профита — нетто-модель). */
  bonusUsdtGroup: z.string().optional(),
  /** Статический бонус в USDT, если группа не захвачена. */
  defaultBonusUsdt: MoneyStr.optional(),
  /**
   * Оператор заявки. Цепочка разрешения: captures[operatorGroup] →
   * defaultOperator → TG-username/email пользователя, применившего событие
   * (только apply-time; при match-time userId ещё неизвестен) →
   * `telegram-listener:<ruleName>` (итог всегда обрезается до 64 символов).
   */
  operatorGroup: z.string().optional(),
  /** Статический оператор (≤64 символов), если группа не захвачена. */
  defaultOperator: z.string().min(1).max(64).optional(),
  /** Информационный курс клиента (clRate) — только из группы, без default. */
  clRateGroup: z.string().optional(),
  /** Информационный курс исполнителя (exRate) — только из группы, без default. */
  exRateGroup: z.string().optional(),
  /**
   * Бизнес-дата заявки из сообщения. Форматы date-переменной regexBuilder:
   * DD.MM.YYYY / DD/MM/YY / DD-MM-YYYY (год 2 или 4 цифры) → YYYY-MM-DD.
   * Группа не задана/не захвачена → дата не отправляется (сервер ставит today).
   */
  dateGroup: z.string().optional(),
  /**
   * true → клиент = счёт, привязанный к чату-источнику: сначала
   * listening_chats.linked_account, затем accounts.telegram_chat_id.
   * ПРИОРИТЕТНЕЕ clientGroup/defaultClient; чат не привязан → validate-отказ.
   */
  clientFromChat: z.boolean().optional(),
  /**
   * Дедуп повторной обработки (edit сообщения порождает новое событие): если из
   * этого же сообщения этим же правилом заявка уже создана (applied-событие
   * с actionType='create-order') — validate возвращает отказ. Default true
   * (отсутствие поля = включено); false — отключить (например для multi-match
   * правил с флагом `g`, где одно сообщение осознанно создаёт несколько заявок).
   */
  skipDuplicate: z.boolean().optional(),
});
export type CreateOrderActionConfig = z.infer<typeof CreateOrderActionConfig>;

/** log-only: параметров нет, просто фиксирует captures в событиях. */
export const LogOnlyActionConfig = z.object({});
export type LogOnlyActionConfig = z.infer<typeof LogOnlyActionConfig>;

// ─── v2 ActionConfigs ───────────────────────────────────────────────────────

/**
 * send-message: отправить сообщение в один/несколько целевых чатов по шаблону
 * с подстановкой captures. Поддерживает HTML/MarkdownV2 форматирование.
 *
 * Отправка в исходный чат разрешена (replyToOriginal делает reply на исходное
 * сообщение). От петли «бот видит своё сообщение → снова матчит» защищает
 * self-filter на входе processIncoming (сообщения своего бота игнорируются).
 *
 * Шаблон использует {placeholder} синтаксис — имена named groups из regex.
 */
export const SendMessageActionConfig = z.object({
  targetChatIds: z.array(z.string().regex(/^-?\d+$/)).min(1).max(20),
  template: z.string().min(1).max(4000),
  parseMode: z.enum(['HTML', 'MarkdownV2', 'plain']).default('HTML'),
  disableNotification: z.boolean().default(false),
  // Для форумных супергрупп (с топиками).
  messageThreadId: z.number().int().optional(),
  // Если true и target chat = source chat — отправить как reply.
  replyToOriginal: z.boolean().default(false),
});
export type SendMessageActionConfig = z.infer<typeof SendMessageActionConfig>;

/**
 * reject-order: отклоняет заявку через rejectOrder() из orders/service.
 * Причина — шаблон с {placeholder}, заполняется captures.
 */
export const RejectOrderActionConfig = z.object({
  orderIdGroup: z.string().default('orderId'),
  reasonTemplate: z.string().min(1).max(500),
});
export type RejectOrderActionConfig = z.infer<typeof RejectOrderActionConfig>;

/**
 * edit-provodka: редактирует pending-проводку (amount). Через editProvodka()
 * из orders/service. Если done — handler возвращает ok:false.
 */
export const EditProvodkaActionConfig = z.object({
  orderIdGroup: z.string().default('orderId'),
  idxGroup: z.string().optional(),
  defaultIdx: z.number().int().min(0).max(3).default(0),
  amountGroup: z.string().optional(),
});
export type EditProvodkaActionConfig = z.infer<typeof EditProvodkaActionConfig>;

/**
 * custom-webhook: POST/PUT/PATCH на внешний URL с JSON-телом (или text).
 * URL и headers — статика; bodyTemplate — с {placeholder} подстановкой.
 * Retry off — на webhook'ах повторы опасны (idempotency не гарантирована
 * на стороне получателя).
 */
export const CustomWebhookActionConfig = z.object({
  url: z.string().url(),
  method: z.enum(['POST', 'PUT', 'PATCH']).default('POST'),
  headers: z.record(z.string(), z.string()).default({}),
  bodyTemplate: z.string().min(1).max(10000),
  timeoutMs: z.number().int().min(1000).max(30000).default(10000),
});
export type CustomWebhookActionConfig = z.infer<typeof CustomWebhookActionConfig>;

/**
 * update-chat-balance: upsert баланса (chat, ccy) → amount из распаршенного
 * сообщения (боты публикуют мультивалютные балансы). Два способа разметки:
 *
 *  1. Построчно: правило с флагом `g` даёт отдельный матч (и event) на каждую
 *     строку/валюту — одна пара `ccyGroup`/`amountGroup`.
 *  2. Целиком: одно совпадение на всё сообщение; visual-конструктор нумерует
 *     переменные по типу (`ccy, ccy2..ccyN` / `amount, amount2..amountN`).
 *     Handler автоматически подхватывает нумерованные продолжения
 *     `${ccyGroup}2`/`${amountGroup}2`, `${ccyGroup}3`/… пока ОБЕ группы
 *     индекса непусты (первая «дыра» — стоп, кап 50 пар) → все валюты
 *     обновляются ОДНИМ событием атомарно.
 *
 * - `targetChatId` отсутствует → баланс пишется исходному чату (source chat).
 * - `ccyAliases` — маппинг сырых токенов сообщения на ISO-коды (например
 *   {"тонн": "USDT"}); ключи сравниваются case-insensitive, применяется к
 *   каждой паре.
 * - `multiPairs` — авто-подхват нумерованных пар; default (не задан) = true.
 *   `false` — строго одна пара (на случай, если группа `ccy2` в правиле
 *   используется для чего-то другого).
 */
export const UpdateChatBalanceActionConfig = z
  .object({
    ccyGroup: z.string().min(1),
    // Обязательна, кроме режима zeroAmount (сумма тогда не берётся из сообщения).
    amountGroup: z.string().min(1).optional(),
    targetChatId: z.string().regex(/^-?\d+$/).optional(),
    ccyAliases: z.record(z.string(), z.string()).optional(),
    multiPairs: z.boolean().optional(),
    // Обнулить валюту из группы ccyGroup — для команд удаления строки баланса
    // (`/b del rub`): бот отвечает «Empty», в ответе нет валют, и без этого
    // правила удалённая строка висела бы в панели (прод 09.09: RUB 1,5 млн 4 суток).
    zeroAmount: z.boolean().optional(),
  })
  .refine((c) => c.zeroAmount === true || !!c.amountGroup, {
    message: 'нужна группа суммы (или режим «обнулить валюту»)',
    path: ['amountGroup'],
  });
export type UpdateChatBalanceActionConfig = z.infer<typeof UpdateChatBalanceActionConfig>;

// ─── Chain & Guards ─────────────────────────────────────────────────────────

/** Одно действие в цепочке правила. */
export const RuleAction = z.object({
  type: TelegramActionType,
  config: z.record(z.string(), z.unknown()),
});
export type RuleAction = z.infer<typeof RuleAction>;

export const GuardOp = z.enum([
  'eq', 'ne',           // равно / не равно (строка или число)
  'gt', 'gte', 'lt', 'lte',  // числовые (через parseMoney для amount-полей)
  'contains',           // подстрока (case-insensitive)
  'matches',            // regex match
  'in',                 // value в списке (массив value)
]);
export type GuardOp = z.infer<typeof GuardOp>;

/**
 * Условие срабатывания правила. Если хоть одно false — правило игнорируется
 * (event НЕ создаётся, autoApply НЕ запускается).
 *
 * Discriminated union по `kind`:
 *  - capture: сравнение значения named group из regex с константой.
 *  - time-range: текущее время попадает в диапазон [fromHour, toHour) в TZ.
 *  - weekday: текущий день недели в списке.
 */
export const RuleGuard = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('capture'),
    field: z.string().min(1),
    op: GuardOp,
    value: z.union([z.string(), z.number(), z.array(z.string())]),
  }),
  z.object({
    kind: z.literal('time-range'),
    // Поддерживает wrap-around (fromHour > toHour означает диапазон через полночь).
    fromHour: z.number().int().min(0).max(23),
    toHour: z.number().int().min(0).max(23),
    timezone: z.string().default('UTC'),
  }),
  z.object({
    kind: z.literal('weekday'),
    days: z.array(z.number().int().min(0).max(6)).min(1).max(7), // 0=Sunday
  }),
]);
export type RuleGuard = z.infer<typeof RuleGuard>;

// ─── Entities ───────────────────────────────────────────────────────────────

export const TelegramListeningChat = z.object({
  /** bigint в DTO передаётся строкой (JSON safe). */
  chatId: z.string(),
  chatTitle: z.string().nullable(),
  kind: z.enum(['group', 'private', 'channel']),
  enabled: z.boolean(),
  linkedAccount: AccountCode.nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TelegramListeningChat = z.infer<typeof TelegramListeningChat>;

export const CreateListeningChatInput = z.object({
  chatId: z.string().regex(/^-?\d+$/, 'chatId must be numeric'),
  chatTitle: z.string().max(200).nullish(),
  kind: z.enum(['group', 'private', 'channel']).default('group'),
  enabled: z.boolean().default(true),
  linkedAccount: AccountCode.nullish(),
  notes: z.string().max(2000).nullish(),
});
export type CreateListeningChatInput = z.infer<typeof CreateListeningChatInput>;

export const UpdateListeningChatInput = z.object({
  chatTitle: z.string().max(200).nullish(),
  kind: z.enum(['group', 'private', 'channel']).optional(),
  enabled: z.boolean().optional(),
  linkedAccount: AccountCode.nullish(),
  notes: z.string().max(2000).nullish(),
});
export type UpdateListeningChatInput = z.infer<typeof UpdateListeningChatInput>;

/**
 * Снапшот баланса чата по валюте (источник — action `update-chat-balance`).
 * Информационный: чаты внешние, с нашими счетами не связаны. PK (chatId, ccy).
 */
export const TelegramChatBalance = z.object({
  /** bigint в DTO передаётся строкой (JSON safe). */
  chatId: z.string(),
  ccy: z.string(),
  amount: MoneyStr,
  updatedAt: z.string(),
  /** Провенанс: event, который последним обновил баланс (nullable — event мог быть удалён). */
  sourceEventId: z.string().uuid().nullable(),
});
export type TelegramChatBalance = z.infer<typeof TelegramChatBalance>;

export const TelegramRule = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().nullable(),
  enabled: z.boolean(),
  pattern: z.string().min(1).max(2000),
  flags: z.string().regex(/^[gimsuy]*$/),
  // Legacy: для обратной совместимости с UI до миграции UI-редактора.
  // В новом API источник истины — `actions[]`.
  actionType: TelegramActionType,
  actionConfig: z.record(z.string(), z.unknown()),
  // v2: цепочка действий. После полной миграции UI legacy поля удалятся.
  actions: z.array(RuleAction).min(1).max(10),
  guards: z.array(RuleGuard).max(20),
  autoApply: z.boolean(),
  priority: z.number().int().min(0).max(999),
  chatIds: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TelegramRule = z.infer<typeof TelegramRule>;

export const UpsertRuleInput = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullish(),
  enabled: z.boolean().default(true),
  pattern: z.string().min(1).max(2000),
  flags: z.string().regex(/^[gimsuy]*$/).default('im'),
  // Старый одношаговый формат — поддерживается для backward compat.
  // Если передан `actions` — приоритет у него.
  actionType: TelegramActionType.optional(),
  actionConfig: z.record(z.string(), z.unknown()).optional(),
  actions: z.array(RuleAction).min(1).max(10).optional(),
  guards: z.array(RuleGuard).max(20).default([]),
  autoApply: z.boolean().default(false),
  priority: z.number().int().min(0).max(999).default(100),
  chatIds: z.array(z.string().regex(/^-?\d+$/)).default([]),
});
export type UpsertRuleInput = z.infer<typeof UpsertRuleInput>;

export const TelegramEventStatus = z.enum(['pending', 'applied', 'dismissed', 'failed']);
export type TelegramEventStatus = z.infer<typeof TelegramEventStatus>;

export const TelegramEvent = z.object({
  id: z.string().uuid(),
  ruleId: z.string().uuid(),
  /** join из rules для удобства UI (избегаем N+1 на фронте). */
  ruleName: z.string(),
  chatId: z.string(),
  chatTitle: z.string().nullable(),
  messageText: z.string().nullable(),
  actionType: TelegramActionType,
  captures: z.record(z.string(), z.string()),
  /** Human-readable превью будущего действия для UI. Структура зависит от actionType. */
  preview: z.record(z.string(), z.unknown()),
  status: TelegramEventStatus,
  appliedBy: z.string().uuid().nullable(),
  appliedAt: z.string().nullable(),
  result: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
});
export type TelegramEvent = z.infer<typeof TelegramEvent>;

// ─── Regex tester (для UI-конструктора) ─────────────────────────────────────

export const TestRegexInput = z.object({
  pattern: z.string().max(2000),
  flags: z.string().regex(/^[gimsuy]*$/),
  sample: z.string().max(10000),
});
export type TestRegexInput = z.infer<typeof TestRegexInput>;

export const TestRegexResult = z.object({
  ok: z.boolean(),
  error: z.string().nullable(),
  matches: z.array(
    z.object({
      match: z.string(),
      index: z.number().int(),
      groups: z.record(z.string(), z.string()),
    }),
  ),
});
export type TestRegexResult = z.infer<typeof TestRegexResult>;

// ─── Message log (для отладки правил) ───────────────────────────────────────

export const TelegramMessageLogEntry = z.object({
  id: z.number().int(),
  chatId: z.string(),
  messageId: z.number().int(),
  fromUserId: z.string().nullable(),
  fromIsBot: z.boolean(),
  text: z.string().nullable(),
  /** Сколько событий породило сообщение суммарно (включая failed и версии после edit). */
  matchedRules: z.number().int(),
  /** Сколько раз сообщение обрабатывалось как edit (0 = только оригинал). */
  editCount: z.number().int(),
  /** ISO-время последнего редактирования (из Telegram edit_date) или null. */
  editDate: z.string().nullable(),
  receivedAt: z.string(),
});
export type TelegramMessageLogEntry = z.infer<typeof TelegramMessageLogEntry>;

// ─── Handler info (для фронта — что доступно, какая config-схема) ──────────

export const TelegramHandlerInfo = z.object({
  type: TelegramActionType,
  name: z.string(),
  description: z.string(),
  /** JSON Schema, сгенерированная из Zod actionConfig-схемы. */
  configSchema: z.record(z.string(), z.unknown()),
});
export type TelegramHandlerInfo = z.infer<typeof TelegramHandlerInfo>;

// ─── Pre-built templates (библиотека правил) ────────────────────────────────

export const RuleTemplateCategory = z.enum(['bank', 'exchange', 'rates', 'custom']);
export type RuleTemplateCategory = z.infer<typeof RuleTemplateCategory>;

export const RuleTemplate = z.object({
  id: z.string().uuid(),
  key: z.string().min(1).max(80),         // стабильный machine-friendly ключ ('tinkoff-incoming-rub')
  name: z.string().min(1).max(120),
  category: RuleTemplateCategory,
  description: z.string().nullable(),
  sampleText: z.string().min(1).max(2000),
  // Segment[] из regexBuilder в jsonb.
  segments: z.array(z.record(z.string(), z.unknown())),
  /**
   * Явные flags правила из шаблона. null → дефолт buildRegexFromSegments
   * ('im'). 'gim' — для шаблонов «матчить каждую строку» (multi-ccy баланс).
   */
  flags: z.string().regex(/^[gimsuy]*$/).nullable(),
  actions: z.array(RuleAction).min(1),
  guards: z.array(RuleGuard).default([]),
  isBuiltin: z.boolean(),
  createdAt: z.string(),
});
export type RuleTemplate = z.infer<typeof RuleTemplate>;

/** Применение template → создание/предзаполнение правила. */
// ─── Bot config (управление токеном/режимом через UI) ──────────────────────

/** DTO для отображения в UI: токен — только маска. */
export const TelegramBotConfigDto = z.object({
  enabled: z.boolean(),
  mode: z.enum(['polling', 'webhook']),
  tokenSet: z.boolean(),
  tokenMask: z.string().nullable(),
  webhookUrl: z.string().nullable(),
  webhookSecretSet: z.boolean(),
  pollingTimeoutS: z.number().int(),
  source: z.enum(['db', 'env', 'none']),
  updatedAt: z.string().nullable(),
});
export type TelegramBotConfigDto = z.infer<typeof TelegramBotConfigDto>;

/**
 * Статус бота для админки: конфиг + live-подключение + runtime-состояние
 * листенера в текущем процессе API. Конфиг читается процессом один раз при
 * старте (hot-reload нет) — поля `listener*` позволяют UI честно показать
 * «конфигурация изменена, нужен рестарт».
 */
export const TelegramBotStatusDto = z.object({
  enabled: z.boolean(),
  mode: z.enum(['polling', 'webhook']),
  tokenSet: z.boolean(),
  webhookUrl: z.string().nullable(),
  webhookSecretSet: z.boolean(),
  botId: z.number().nullable(),
  botUsername: z.string().nullable(),
  connected: z.boolean(),
  error: z.string().nullable(),
  /** Транспорт (polling/webhook) реально запущен в текущем процессе API. */
  listenerRunning: z.boolean(),
  /** Режим запущенного транспорта; null — если листенер не запущен. */
  listenerMode: z.enum(['polling', 'webhook']).nullable(),
  /**
   * ISO-момент, когда процесс API прочитал bot_config (= старт процесса).
   * Если config.updatedAt > listenerStartedAt — изменения ещё не применены.
   */
  listenerStartedAt: z.string().nullable(),
});
export type TelegramBotStatusDto = z.infer<typeof TelegramBotStatusDto>;

/**
 * Запрос на обновление config. `null` означает «очистить», поле отсутствует —
 * «не менять» (PATCH-семантика).
 */
export const UpdateTelegramBotConfigInput = z.object({
  enabled: z.boolean().optional(),
  mode: z.enum(['polling', 'webhook']).optional(),
  /** Новый токен в открытом виде — будет зашифрован в БД. */
  token: z.string().min(1).max(500).nullish(),
  webhookUrl: z.string().url().nullish(),
  webhookSecret: z.string().min(1).max(256).nullish(),
  pollingTimeoutS: z.number().int().min(1).max(50).optional(),
});
export type UpdateTelegramBotConfigInput = z.infer<typeof UpdateTelegramBotConfigInput>;

/** Тест подключения с заданным токеном (или текущим из БД). */
export const TestBotTokenInput = z.object({
  /** Опциональный — если не передан, используется токен из БД/env. */
  token: z.string().min(1).max(500).optional(),
});
export const TestBotTokenResult = z.object({
  ok: z.boolean(),
  botId: z.number().nullable(),
  botUsername: z.string().nullable(),
  error: z.string().nullable(),
});
export type TestBotTokenInput = z.infer<typeof TestBotTokenInput>;
export type TestBotTokenResult = z.infer<typeof TestBotTokenResult>;

export const ApplyTemplateInput = z.object({
  templateKey: z.string().min(1),
  // Новые поля поверх template — оператор может переопределить.
  name: z.string().min(1).max(100).optional(),
  chatIds: z.array(z.string().regex(/^-?\d+$/)).default([]),
});
export type ApplyTemplateInput = z.infer<typeof ApplyTemplateInput>;

// ─── Chat sync (синхронизация чатов из внешнего read-only API) ───────────────

/** Группа из ответа внешнего API. `telegram_channel_id` — СЫРОЙ id (без -100). */
export const ChatSyncGroup = z.object({
  title: z.string(),
  telegram_channel_id: z.number().int(),
});
export type ChatSyncGroup = z.infer<typeof ChatSyncGroup>;

export const ChatSyncGroupsResponse = z.object({ groups: z.array(ChatSyncGroup) });
export type ChatSyncGroupsResponse = z.infer<typeof ChatSyncGroupsResponse>;

/** Конфиг синхронизации для UI — БЕЗ токена, только флаг `tokenSet`. */
export const ChatSyncConfigDto = z.object({
  enabled: z.boolean(),
  apiUrl: z.string().nullable(),
  tokenSet: z.boolean(),
  lastSyncAt: z.string().nullable(),
  lastSyncStatus: z.enum(['ok', 'failed']).nullable(),
  lastSyncError: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type ChatSyncConfigDto = z.infer<typeof ChatSyncConfigDto>;

/** apiToken: строка → перешифровать; null → очистить; отсутствует → не менять. */
export const UpdateChatSyncConfigInput = z.object({
  enabled: z.boolean().optional(),
  apiUrl: z.string().url().nullish(),
  apiToken: z.string().min(1).nullish(),
});
export type UpdateChatSyncConfigInput = z.infer<typeof UpdateChatSyncConfigInput>;

/** Итог синхронизации (ответ ручного триггера и авто-тика). */
export const ChatSyncOutcome = z.object({
  status: z.enum(['ok', 'failed']),
  added: z.number().int(),
  updated: z.number().int(),
  total: z.number().int(),
  error: z.string().nullable(),
});
export type ChatSyncOutcome = z.infer<typeof ChatSyncOutcome>;

// MoneyStr re-export для удобства (handlers нормализуют через D).
export { MoneyStr };
